import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress, Paper, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { CopyableFieldRow } from 'features/authorization/components/CreateAuthorizationModal/CopyableFieldRow';
import { TransactionAuthorizationContext } from 'features/authorization/types/authorizationGuide';
import { canShowCardSensitiveInfo } from 'features/transaction-details/mappers';
import { getCardInfo } from 'features/transactions/services';
import { IFlagShowCardInfo } from 'features/transactions/types';

interface DLocalTransactionCopyPanelProps {
	transactionContext: TransactionAuthorizationContext;
	flagShowCardInfo: IFlagShowCardInfo;
	isAdminOrOperator: boolean;
	onCopied?: (message: string) => void;
}

const InfoRow = ({ label, value }: { label: string; value: string }): JSX.Element => (
	<Box
		sx={{
			py: 0.75,
			px: 1,
			borderRadius: 1,
			bgcolor: 'grey.100',
			border: '1px solid',
			borderColor: 'divider',
		}}
	>
		<Typography variant="caption" color="text.secondary" display="block">
			{label}
		</Typography>
		<Typography variant="body2" color="text.secondary">
			{value || '—'}
		</Typography>
	</Box>
);

export const DLocalTransactionCopyPanel = ({
	transactionContext,
	flagShowCardInfo,
	isAdminOrOperator,
	onCopied,
}: DLocalTransactionCopyPanelProps): JSX.Element => {
	const [cardFullInfo, setCardFullInfo] = useState<{ cardNumber: number; cardSecurityCode: number } | null>(null);
	const [loadingCard, setLoadingCard] = useState(false);
	const [revealed, setRevealed] = useState(false);
	const cardNumberCopyButtonRef = useRef<HTMLButtonElement>(null);

	const canReveal = isAdminOrOperator && canShowCardSensitiveInfo(flagShowCardInfo);

	const focusCardNumberCopy = (): void => {
		requestAnimationFrame(() => {
			cardNumberCopyButtonRef.current?.focus();
		});
	};

	const handleRevealCard = async (): Promise<void> => {
		if (cardFullInfo) {
			setRevealed(true);
			focusCardNumberCopy();
			return;
		}
		setLoadingCard(true);
		try {
			const response = await getCardInfo(transactionContext.transactionId);
			setCardFullInfo({
				cardNumber: response.data.cardNumber,
				cardSecurityCode: response.data.cardSecurityCode,
			});
			setRevealed(true);
		} catch {
			onCopied?.('Error al obtener datos de tarjeta');
		} finally {
			setLoadingCard(false);
		}
	};

	useEffect(() => {
		if (revealed && cardFullInfo) {
			focusCardNumberCopy();
		}
	}, [revealed, cardFullInfo]);

	const cardNumberValue =
		revealed && cardFullInfo ? cardFullInfo.cardNumber.toString() : transactionContext.cardNumberFormatted;

	const cvcValue =
		revealed && cardFullInfo ? cardFullInfo.cardSecurityCode.toString() : transactionContext.cardSecurityCodeFormatted;

	return (
		<Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
			<Typography variant="subtitle2" fontWeight="bold" gutterBottom>
				Datos para pegar en DLocal
			</Typography>
			<Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
				Copiá solo número de tarjeta, vencimiento y CVC en el checkout de DLocal.
			</Typography>

			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
				{canReveal && !revealed && (
					<Button
						size="small"
						variant="outlined"
						startIcon={loadingCard ? <CircularProgress size={14} /> : <VisibilityIcon />}
						onClick={handleRevealCard}
						disabled={loadingCard}
						fullWidth
						sx={{ justifyContent: 'flex-start' }}
					>
						{loadingCard ? 'Cargando...' : 'Revelar nº tarjeta y CVC para copiar'}
					</Button>
				)}
				<CopyableFieldRow
					label="Nº tarjeta"
					value={cardNumberValue}
					onCopied={onCopied}
					copyButtonRef={cardNumberCopyButtonRef}
				/>
				<CopyableFieldRow label="Vencimiento" value={transactionContext.cardExpiration} onCopied={onCopied} />
				<CopyableFieldRow label="CVC" value={cvcValue} onCopied={onCopied} />

				<Typography variant="caption" color="text.secondary" sx={{ mt: 1, mb: 0.5 }}>
					Referencia (no se pegan en DLocal)
				</Typography>
				<InfoRow label="Nombre titular (tarjeta)" value={transactionContext.cardholder} />
				{transactionContext.document && (
					<InfoRow label="Documento titular (tarjeta)" value={transactionContext.document} />
				)}
				<InfoRow label="Cuotas" value={String(transactionContext.fee)} />
			</Box>
		</Paper>
	);
};
